import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { isString } from 'lodash';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { surfaceApiErrors } from '../../../utils/apiFieldErrors';

const createSalesInvoice = async ({ payload }) => {
  const profileId = payload?.accounting_supplier_party?.profile_id;
  if (!profileId) {
    throw new Error('SUPPLIER_PROFILE_REQUIRED');
  }
  const response = await apiClient.post(`/sales-invoices/${profileId}`, payload);
  return response.data;
};

const createPurchaseInvoice = async ({ payload }) => {
  const profileId = payload?.accounting_customer_party?.profile_id;
  if (!profileId) {
    throw new Error('CUSTOMER_PROFILE_REQUIRED');
  }

  const response = await apiClient.post(`/purchase-invoices/${profileId}`, payload);
  return response.data;
};

export const useCreateInvoiceHandler = ({ onSuccess, isPurchase = false }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { eInvoiceForm } = useTranslatedMessages();
  const messages = eInvoiceForm?.invoice_handlers || {};
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: isPurchase ? createPurchaseInvoice : createSalesInvoice,
    onSuccess: (data, variables, context) => {
      dispatch(showSnackbar({ message: messages.invoice_created_success }));
      queryClient.invalidateQueries({ queryKey: [isPurchase ? 'purchase-invoices' : 'sales-invoices'] });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const apiErrors = error.response?.data?.errors;
        if (apiErrors) {
          surfaceApiErrors({
            setFormError: setError,
            dispatch,
            showSnackbarAction: showSnackbar,
            apiErrors,
          });
          return;
        }
        dispatch(
          showSnackbar({
            message: error.response?.data?.message ?? error.message ?? 'An error occurred.',
            severity: 'error',
          }),
        );
      } else {
        const message =
          error.message === 'SUPPLIER_PROFILE_REQUIRED'
            ? messages.supplier_profile_required
            : error.message === 'CUSTOMER_PROFILE_REQUIRED'
              ? messages.customer_profile_required
              : isString(error.message)
                ? error.message
                : JSON.stringify(error.message);
        dispatch(showSnackbar({ message, severity: 'error' }));
      }
    },
  });

  const onSubmit = async (payload) => {
    const profileId = isPurchase
      ? payload?.accounting_customer_party?.profile_id
      : payload?.accounting_supplier_party?.profile_id;
    if (!profileId) {
      const msg = isPurchase ? messages.customer_profile_required : messages.supplier_profile_required;
      dispatch(showSnackbar({ message: msg, severity: 'error' }));
      return;
    }
    mutation.mutate({ payload });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
